<script setup lang="ts">
import { ref, reactive, computed, nextTick, watch, ComponentPublicInstance } from 'vue';
import { useWindowScroll } from '@vueuse/core';
import { process, SortDescriptor } from '@progress/kendo-data-query';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import useHRAdminGenderStore from '@/stores/HRAdmin/genders';
import AdminEditControls from '@/views/Partials/AdminSidebarEditControls.vue';
import {
  Grid, GridDataStateChangeEvent, GridColumnProps, GridSortChangeEvent,
} from '@progress/kendo-vue-grid';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxForm from '@/components/Molecules/Field/ConnxForm.vue';
import { InputType } from '@/interfaces/InputFields';
import { Gender } from '@/interfaces/CBW/GenderResponse';
import useApplicationStore from '@/stores/application';
import useFlashMessageStore from '@/stores/flash-messages';
import { uniqueRules } from '@/lib/generate-rules';
import { PayrollSystemMapping } from '@/interfaces/CBW/PayrollDatabaseResponse';

const applicationStore = useApplicationStore();
const genderStore = useHRAdminGenderStore();
const messageStore = useFlashMessageStore();
const windowScroll = useWindowScroll({ behavior: 'smooth' })
await genderStore.init();

const columns = computed(() => [
  {
    field: 'description',
    title: 'Gender',
    cell: 'GenderCellTemplate',
  },
] as GridColumnProps[]);

const payrollColumns = computed(() => [
  {
    field: null,
    title: 'Payroll Database',
    cell: 'CompanyNameTemplate',
  },
  {
    field: 'payrollGenderValue',
    title: 'Payroll Gender Value',
    cell: 'EditTemplate',
  },
] as GridColumnProps[]);

const newGender = reactive({
  description: '',
  payrollSystemMapping: genderStore.payrollCompanies.map((payroll) => ({
    ...payroll,
    payrollGenderValue: '',
  }))
});

const clearErrorIfEmpty = () => {
  if (newGender.description === '' && newGender.payrollSystemMapping.filter((p) => p.payrollGenderValue !== '').length === 0) {
    key.value += 1;
  }
}

const selectedGender = ref<Gender | null>();
const confirmationModalOpen = ref(false);
const selectedID = ref<null | number>();
const key = ref(0);
const loadingPayroll = ref(false);
const selectedField = ref('editing');
const sort = ref<SortDescriptor[]>([]);

const genderBeingEdited = computed(() => {
  return genderStore.genders.find(g => g.id === selectedID.value);
})
const payrollGridData = computed(() => {
  if (selectedID.value && selectedGender.value) {
    return selectedGender.value.payrollSystemMapping
  }

  return newGender.payrollSystemMapping;
})

const cancelEdit = async () => {
  await clearForm();
  windowScroll.y.value = 0;
};

const genderRecords = computed(() => {
  const genders = genderStore.sortedGenders.map((record) => ({
    ...record,
    editing: selectedID.value === record.id,
  }));

  return process(genders, {
    sort: sort.value,
  });
});

const canDeleteGender = (gender: Gender) => {
  return !genderStore.constantGenders.includes(gender.description.toLowerCase())
}

const constantGender = computed(() => {
  if (genderBeingEdited.value) {
    return !canDeleteGender(genderBeingEdited.value);
  }

  return false;
})

const genderDescriptions = computed(() => genderStore.sortedGenders.map(g => g.description.toLowerCase()));

const clearForm = async () => {
  selectedID.value = null;
  newGender.description = '';
  newGender.payrollSystemMapping = [];
  // clear the current values
  await nextTick();
  newGender.payrollSystemMapping = genderStore.payrollCompanies.map((payroll) => ({
    ...payroll,
    payrollGenderValue: '',
  }))
  key.value += 1;
}

const toggleConfirmationModal = () => {
  if (confirmationModalOpen.value) {
    clearForm();
  }
  confirmationModalOpen.value = !confirmationModalOpen.value
};

const dataStateChange = (event: GridDataStateChangeEvent | GridSortChangeEvent) => {
  if (Object.keys(event).includes('sort')) {
    sort.value = (event as GridSortChangeEvent).sort
  }
};

const removeGender = async () => {
  if (selectedID.value) {
    await genderStore.deleteGender(selectedID.value);
    messageStore.pushNotification({
      id: `GENDER_${selectedID.value}`,
      type: 'success',
      message: `Gender '${selectedGender.value?.description}' deleted successfully.`,
    });
  }
  await clearForm();
  toggleConfirmationModal();
}

const getPayrollCompany = (coId: number) => {
  return genderStore.payrollCompanies.find(company => company.id === coId);
}

const saveGender = async (saveAction: 'new' | 'edit') => {
  applicationStore.setLoading(true);

  let insertedGenderId: null | number = null;
  if (selectedID.value) {
    insertedGenderId = selectedID.value
    // Updating gender
    const updatedGender = await genderStore.updateGender(selectedGender.value as Gender);
    if (updatedGender) {
      messageStore.pushNotification({
        id: `GENDER_${insertedGenderId}`,
        type: 'success',
        message: `Gender '${updatedGender.description}' updated successfully.`,
      });
      for (const psm of selectedGender.value?.payrollSystemMapping!) {
        if (psm.id) {
          // if the PSM has an ID, it exists, otherwise its a blank.
          if (!psm.payrollGenderValue) {

            // If no value, lets delete the mapping.
            await genderStore.deletePayrollSystemMapping(selectedID.value!, psm.id);
          } else {
            // Otherwise lets just update it.
            await genderStore.updatePayrollSystemMapping(selectedID.value!, psm.id, psm.payrollGenderValue);
          }
        } else if (psm.payrollGenderValue) {
          await genderStore.insertPayrollSystemMapping(selectedID.value!, psm.coId, psm.payrollGenderValue);
        }
      }
    }
  } else {
    // New gender
    const insertedGender = await genderStore.insertGender(newGender.description);
    if (insertedGender) {
      messageStore.pushNotification({
        id: `GENDER_${insertedGenderId}`,
        type: 'success',
        message: `Gender '${insertedGender.description}' created successfully.`,
      });
      insertedGenderId = insertedGender.id;
      for (const psm of newGender.payrollSystemMapping.filter(psm => psm.payrollGenderValue)) {
        await genderStore.insertPayrollSystemMapping(insertedGender.id, psm.id, psm.payrollGenderValue);
      }
    }
  }

  selectedID.value = insertedGenderId;
  await nextTick();
  applicationStore.setLoading(false);
  await clearForm();
  await nextTick();
  windowScroll.y.value = 0;
}

const showDeleteConfirmationOpen = (gender: Gender) => {
  selectedID.value = gender.id;
  confirmationModalOpen.value = true;
}

const saveDisabled = computed(() => {
  let description = '';
  if (selectedGender.value) {
    const editedGenderDescription = genderBeingEdited.value?.description.toLowerCase();
    const uniqueDescriptions = genderDescriptions.value.filter(desc => desc !== editedGenderDescription);
    description = selectedGender.value.description.trim().toLowerCase();

    if (uniqueDescriptions.includes(description)) {
      return true;
    }

    return description === '';
  }

  description = newGender.description.trim().toLowerCase();

  if (genderDescriptions.value.includes(description)) {
    return true;
  }

  return description === '';
});

watch(selectedID, async () => {
  loadingPayroll.value = true;
  if (selectedID.value) {
    await genderStore.getPayrollSystemMapping(selectedID.value!);
  }

  if (selectedID.value) {
    selectedGender.value = {
      ...genderStore.genders.find(g => g.id === selectedID.value)!,
    };
  } else {
    selectedGender.value = null;
  }
  loadingPayroll.value = false;
});

const toggleEdit = (gender: Gender, editing: boolean) => {
  if (editing) {
    selectedID.value = gender.id;
  } else {
    selectedID.value = null;
  }
}

const getUniqueGenderPayrollKey = (payroll: PayrollSystemMapping) => {
  return [
    'gender-payroll',
    payroll.coId,
    payroll.id,
    key.value
  ].filter(i => i !== undefined).join('-')
}

</script>
<template>
  <connx-confirmation-modal
    :open="confirmationModalOpen"
    @close="toggleConfirmationModal()"
    @confirmed="removeGender()"
  ></connx-confirmation-modal>

  <div class="tw-flex tw-flex-wrap tw-space-x-4">
    <div class="tw-order-2 md:tw-order-1">
      <grid
        ref="grid"
        class="tw-w-152"
        scrollable="none"
        :data-items="genderRecords"
        :row-class="() => 'tw-group'"
        :sortable="true"
        :sort="sort"
        :selectable="true"
        :selected-field="selectedField"
        :columns="columns"
        @datastatechange="dataStateChange"
        @sortchange="dataStateChange"
      >
        <template #GenderCellTemplate="{ props }">
          <td class="k-table-td">
            <div class="tw-flex tw-items-between tw-items-center tw-w-full">
              <span class="tw-flex-shrink-0">{{ props.dataItem.description }}</span>
              <admin-edit-controls
                :editing="props.dataItem.editing"
                :show-save="false"
                class="tw-w-full"
                :delete-disabled="!canDeleteGender(props.dataItem)"
                @saved="saveGender('edit')"
                @editing="(editing) => toggleEdit(props.dataItem, editing)"
                @deleted="showDeleteConfirmationOpen(props.dataItem)"
              ></admin-edit-controls>
            </div>
          </td>
        </template>
      </grid>
    </div>
    <connx-form
      ref="form"
      class="tw-order-2 md:tw-order-2"
      @valid-submission="genderBeingEdited ? saveGender('edit') : saveGender('edit')"
    >
      <h5 class="tw-mb-4">
        {{ genderBeingEdited ? `Editing ${genderBeingEdited.description}` : 'New Gender' }}
      </h5>

      <div class="tw-pb-10">
        <connx-field
          autofocus
          :disabled="constantGender"
          :key="`editing-gender-${key}`"
          v-if="selectedGender && !loadingPayroll"
          :maxlength="50"
          label-inline
          :type="InputType.Text"
          required
          v-model="selectedGender.description"
          name="gender"
          label="Gender"
          class="tw-min-w-96"
          :rules="uniqueRules('Gender', genderDescriptions, genderBeingEdited?.description, true, 'Please enter a gender value.')"
        >
        </connx-field>
        <connx-field
          autofocus
          v-else
          :maxlength="50"
          :key="`new-gender-${key}`"
          label-inline
          @blur="clearErrorIfEmpty"
          :type="InputType.Text"
          required
          v-model="newGender.description"
          name="gender"
          label="Gender"
          class="tw-min-w-96"
          :rules="uniqueRules('Gender', genderDescriptions, '', true, 'Please enter a gender value.')"
        >
        </connx-field>
        <h5 class="tw-my-4">Payroll System Mapping</h5>
        <grid
          ref="payrollGrid"
          class="tw-w-152"
          scrollable="none"
          :data-items="payrollGridData"
          :row-class="() => 'tw-group'"
          :sortable="true"
          :columns="payrollColumns"
        >
          <template #CompanyNameTemplate="{ props }">
            <td class="k-table-td">
              {{ props.dataItem.companyName ?? getPayrollCompany(props.dataItem.coId)?.companyName }}
            </td>
          </template>
          <template #EditTemplate="{ props }">
            <td class="k-table-td">
              <connx-field
                :maxlength="50"
                label-inline
                :type="InputType.Text"
                :key="getUniqueGenderPayrollKey(props.dataItem)"
                v-model="props.dataItem.payrollGenderValue"
                :name="getUniqueGenderPayrollKey(props.dataItem)"
                class="tw-min-w-64"
              >
              </connx-field>
            </td>
          </template>
        </grid>
        <div
          v-if="selectedID"
          class="tw-mt-4 tw-flex tw-gap-x-4"
        >
          <connx-button
            :disabled="saveDisabled"
            type="submit"
            icon-position="before"
            button-style="success"
            icon="fas fa-check"
          >
            Save
          </connx-button>
          <connx-button
            @click="cancelEdit()"
            button-style="white"
          >
            Cancel
          </connx-button>
        </div>
        <div
          v-else
          class="tw-mt-4 tw-flex tw-gap-x-4"
        >
          <connx-button
            button-style="brand-primary"
            icon="fas fa-plus"
            button-size="sm"
            icon-position="before"
            type="submit"
            :disabled="saveDisabled"
          >
            Add
          </connx-button>
          <connx-button
            type="button"
            button-style="white"
            @click="cancelEdit()"
          >
            Clear
          </connx-button>
        </div>
      </div>
    </connx-form>
  </div>
</template>